import React, { useState, useRef, useEffect } from "react";
import { Button } from "../../../../ui/components/Button";
import { Accordion } from "../../../../ui/components/Accordion";
import { IconWithBackground } from "../../../../ui/components/IconWithBackground";
import {
  FeatherUpload,
  FeatherUploadCloud,
  FeatherAlertTriangle,
  FeatherCheckCircle,
  FeatherXCircle,
} from "@subframe/core";
import {
  uploadCustomersFile,
  uploadVendorsFile,
} from "../../data/api/customerVendorUploadApi";
import type {
  BulkUploadIssueRowDto,
  BulkUploadResultDataDto,
} from "../../../masters/data/dto/BulkUploadDto";
import {
  bulkUploadDtoHasProblems,
  bulkUploadIssuePrimaryFromDto,
  expandFlatBulkUploadDtoBuckets,
} from "../../../masters/presentation/utils/bulkUploadOutcomeHelpers";

interface UploadSectionProps {
  type: "customers" | "vendors";
  onError?: (message: string) => void;
  onSuccess?: (message: string) => void;
  onWarning?: (message: string) => void;
}

const UploadSection: React.FC<UploadSectionProps> = ({
  type,
  onError,
  onSuccess,
  onWarning,
}) => {
  const fileInputRef = useRef<HTMLInputElement>(null);
  const resultsScrollRef = useRef<HTMLDivElement>(null);
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [isUploading, setIsUploading] = useState(false);
  const [uploadResult, setUploadResult] =
    useState<BulkUploadResultDataDto | null>(null);
  const [uploadError, setUploadError] = useState<string | null>(null);

  const dtoHasProblems = (d: BulkUploadResultDataDto) =>
    bulkUploadDtoHasProblems(d);

  useEffect(() => {
    if (!uploadResult || !dtoHasProblems(uploadResult)) return;
    const timer = window.setTimeout(() => {
      const el = resultsScrollRef.current;
      if (!el) return;
      el.scrollIntoView({ behavior: "smooth", block: "end" });
    }, 150);
    return () => window.clearTimeout(timer);
  }, [uploadResult]);

  const handleFileSelect = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file) {
      const validExtensions = [".xlsx", ".xls"];
      const fileExtension = file.name
        .substring(file.name.lastIndexOf("."))
        .toLowerCase();
      if (!validExtensions.includes(fileExtension)) {
        setUploadError("Only Excel files (.xlsx, .xls) are allowed");
        return;
      }
      setSelectedFile(file);
      setUploadError(null);
      setUploadResult(null);
    }
  };

  const handleUpload = async () => {
    if (!selectedFile) return;

    setUploadError(null);
    setUploadResult(null);

    try {
      setIsUploading(true);

      const result =
        type === "customers"
          ? await uploadCustomersFile(selectedFile)
          : await uploadVendorsFile(selectedFile);

      const data = result.data as BulkUploadResultDataDto;
      setUploadResult(data);

      const problems = dtoHasProblems(data);
      const created = data.created_count ?? data.inserted_count;
      const label = type === "customers" ? "customers" : "vendors";

      if (created > 0 && !problems) {
        onSuccess?.(`Imported ${created} ${label}. All rows succeeded.`);

        return;
      }

      if (created > 0 && problems) {
        const msg = `Imported ${created} ${label}; some rows need attention in the sections below.`;
        onWarning?.(msg);
        return;
      }

      if (problems) {
        onError?.("No rows were imported. Review the outcomes below.");
        return;
      }

      onError?.("No rows were imported. Adjust the file and try again.");
    } catch (error: unknown) {
      const errorMessage =
        error instanceof Error
          ? error.message
          : "Upload failed. Please try again.";
      setUploadError(errorMessage);
      onError?.(errorMessage);
    } finally {
      setIsUploading(false);
    }
  };

  const handleReset = () => {
    setSelectedFile(null);
    setUploadResult(null);
    setUploadError(null);
    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }
  };

  const title = type === "customers" ? "Upload Customers" : "Upload Vendors";
  const description =
    type === "customers"
      ? "Upload your filled customers Excel file to bulk import into the system"
      : "Upload your filled vendors Excel file to bulk import into the system";

  const bucketed = uploadResult
    ? expandFlatBulkUploadDtoBuckets(uploadResult)
    : null;
  const vf = bucketed?.validationFailures ?? [];
  const sk = bucketed?.skippedExisting ?? [];
  const otherRows = bucketed?.otherFailures ?? [];
  const otherErrorStatCount = bucketed?.otherErrorStatCount ?? otherRows.length;

  let createdUi: {
    row: number;
    display_name?: string;
    entity_id?: number;
  }[] = [];

  if (uploadResult) {
    const apiC = uploadResult.created_records ?? [];
    if (apiC.length > 0) {
      createdUi = apiC.map((r) => ({
        row: r.row,
        display_name: r.display_name,
        entity_id: r.entity_id,
      }));
    } else if (
      uploadResult.inserted_records &&
      uploadResult.inserted_records.length > 0
    ) {
      createdUi = uploadResult.inserted_records.map((record) => ({
        row: record.row,
        display_name:
          type === "customers"
            ? record.customer_name
            : record.vendor_legal_name,
        entity_id: type === "customers" ? record.customer_id : record.vendor_id,
      }));
    }
  }

  const showFlatIssuesOnly =
    uploadResult != null &&
    uploadResult.errors.length > 0 &&
    vf.length === 0 &&
    sk.length === 0 &&
    otherRows.length === 0;

  return (
    <div className="flex w-full flex-col items-start gap-6">
      <div className="flex w-full flex-col items-start gap-2">
        <span className="text-heading-2 font-heading-2 text-default-font">
          {title}
        </span>
        <span className="text-body font-body text-subtext-color">
          {description}
        </span>
      </div>

      <div className="flex w-full flex-col items-start gap-4">
        <div
          className="flex w-full flex-col items-center justify-center gap-3 rounded-md border border-dashed border-brand-600 bg-brand-50 px-6 py-8 cursor-pointer"
          onClick={() => fileInputRef.current?.click()}
        >
          <IconWithBackground size="large" icon={<FeatherUploadCloud />} />
          <div className="flex flex-col items-center justify-center gap-1">
            <span className="text-body-bold font-body-bold text-default-font text-center">
              {selectedFile
                ? selectedFile.name
                : "Click to select file or drag to upload"}
            </span>
            <span className="text-caption font-caption text-subtext-color text-center">
              Supported formats: Excel (.xlsx, .xls) (Max 10MB)
            </span>
          </div>
        </div>

        <input
          ref={fileInputRef}
          type="file"
          accept=".xlsx,.xls"
          style={{ display: "none" }}
          onChange={handleFileSelect}
        />

        {selectedFile && (
          <div className="flex w-full items-center justify-between gap-4">
            <Button
              variant="neutral-tertiary"
              onClick={handleReset}
              disabled={isUploading}
            >
              Clear
            </Button>
            <Button
              icon={
                isUploading ? (
                  <div className="animate-spin">⏳</div>
                ) : (
                  <FeatherUpload />
                )
              }
              onClick={handleUpload}
              disabled={isUploading}
            >
              {isUploading ? "Uploading..." : "Upload File"}
            </Button>
          </div>
        )}

        {uploadError && (
          <div className="flex w-full items-start gap-3 rounded-md border border-solid border-error-200 bg-error-50 px-4 py-3">
            <FeatherAlertTriangle className="h-5 w-5 text-error-600 flex-shrink-0 mt-0.5" />
            <div className="flex flex-col gap-1">
              <span className="text-body-bold font-body-bold text-error-700">
                Upload Failed
              </span>
              <span className="text-body font-body text-error-600">
                {uploadError}
              </span>
            </div>
          </div>
        )}

        {uploadResult ? (
          <div
            ref={resultsScrollRef}
            className="flex w-full flex-col items-start gap-4 rounded-md border border-solid border-neutral-border bg-default-background p-4"
          >
            <div className="flex w-full items-center gap-2">
              {(() => {
                const ok =
                  uploadResult.inserted_count > 0 &&
                  !dtoHasProblems(uploadResult);
                if (ok) {
                  return (
                    <FeatherCheckCircle className="h-5 w-5 text-success-600" />
                  );
                }
                if (
                  uploadResult.inserted_count > 0 &&
                  dtoHasProblems(uploadResult)
                ) {
                  return (
                    <FeatherAlertTriangle className="h-5 w-5 text-warning-600" />
                  );
                }
                return <FeatherXCircle className="h-5 w-5 text-error-600" />;
              })()}
              <span className="text-body-bold font-body-bold text-default-font">
                Upload Results
              </span>
            </div>

            <p className="text-caption font-caption text-subtext-color">
              {uploadResult.processed_rows} non-empty row
              {uploadResult.processed_rows !== 1 ? "s" : ""} processed out of{" "}
              {uploadResult.total_rows} · {uploadResult.validated_successful}{" "}
              passed checks before import attempts
            </p>

            <div className="grid w-full grid-cols-2 gap-4 sm:grid-cols-2 lg:grid-cols-4">
              <Stat
                label="Created"
                value={
                  uploadResult.created_count ?? uploadResult.inserted_count
                }
                success
              />
              <Stat
                label="Already in system"
                value={uploadResult.already_exists_count ?? sk.length}
              />
              <Stat
                label="Fix in Excel"
                value={
                  uploadResult.validation_failed_count ??
                  uploadResult.validated_failed
                }
                error={
                  (uploadResult.validation_failed_count ??
                    uploadResult.validated_failed) > 0
                }
              />
              <Stat
                label="Other errors"
                value={otherErrorStatCount}
                error={otherErrorStatCount > 0}
              />
            </div>

            <div className="flex w-full flex-col gap-2 text-caption font-caption text-subtext-color">
              <span>
                Rows that pass validation import immediately; unsuccessful rows
                appear per category below.
              </span>
            </div>

            <div className="flex w-full flex-col gap-2">
              {createdUi.length > 0 ? (
                <Accordion
                  className="w-full overflow-hidden rounded-md border border-solid border-success-200 bg-success-50"
                  trigger={
                    <div className="flex w-full items-center gap-2 px-4 py-3">
                      <span className="text-body-bold font-body-bold text-success-900">
                        Created ({createdUi.length})
                      </span>
                      <Accordion.Chevron className="ml-auto text-success-900" />
                    </div>
                  }
                  defaultOpen
                >
                  <div className="border-t border-solid border-success-200 px-4 py-3 max-h-48 overflow-y-auto text-sm">
                    {createdUi.map((r, idx) => (
                      <div key={idx} className="flex items-center gap-2 py-1">
                        <FeatherCheckCircle className="h-4 w-4 text-success-600" />
                        <span>
                          Row {r.row}: {r.display_name || "—"}
                        </span>
                      </div>
                    ))}
                  </div>
                </Accordion>
              ) : null}

              {sk.length > 0 ? (
                <AccordionSection
                  title={`Already in system (${sk.length})`}
                  defaultOpen={
                    uploadResult.inserted_count === 0 &&
                    vf.length === 0 &&
                    sk.length > 0 &&
                    otherRows.length === 0
                  }
                  rows={sk}
                />
              ) : null}

              {vf.length > 0 ? (
                <AccordionSection
                  title={`Fix in Excel (${vf.length})`}
                  variant="danger"
                  defaultOpen
                  rows={vf}
                />
              ) : null}

              {otherRows.length > 0 ? (
                <AccordionSection
                  title={`Other errors (${otherErrorStatCount})`}
                  variant="danger"
                  rows={otherRows}
                />
              ) : null}

              {showFlatIssuesOnly ? (
                <AccordionSection
                  title={`Issues (${uploadResult.errors.length})`}
                  variant="danger"
                  defaultOpen
                  rows={uploadResult.errors}
                />
              ) : null}
            </div>
          </div>
        ) : null}
      </div>
    </div>
  );
};

function Stat(props: {
  label: string;
  value: number;
  success?: boolean;
  error?: boolean;
}) {
  let colorClass = "text-default-font";
  if (props.success) colorClass = "text-success-600";
  if (props.error) colorClass = "text-error-600";
  return (
    <div className="flex flex-col gap-1">
      <span className="text-caption font-caption text-subtext-color">
        {props.label}
      </span>
      <span className={`text-body-bold font-body-bold ${colorClass}`}>
        {props.value}
      </span>
    </div>
  );
}

function AccordionSection(props: {
  title: string;
  rows: BulkUploadIssueRowDto[];
  defaultOpen?: boolean;
  variant?: "neutral" | "danger";
}) {
  const variant = props.variant ?? "neutral";
  const box =
    variant === "danger"
      ? "border-error-200 bg-error-50"
      : "border-neutral-border bg-neutral-50";
  const titleCls =
    variant === "danger" ? "text-error-800" : "text-default-font";
  const chevronCls = variant === "danger" ? "text-error-800" : "";

  return (
    <Accordion
      className={`w-full overflow-hidden rounded-md border border-solid ${box}`}
      trigger={
        <div className="flex w-full items-center gap-2 px-4 py-3">
          <span className={`text-body-bold font-body-bold ${titleCls}`}>
            {props.title}
          </span>
          <Accordion.Chevron className={`ml-auto ${chevronCls}`} />
        </div>
      }
      defaultOpen={props.defaultOpen ?? false}
    >
      <div
        className={
          variant === "danger"
            ? "border-t border-solid border-error-200 px-4 py-3"
            : "border-t border-solid border-neutral-border px-4 py-3"
        }
      >
        <DtoIssueBlocks rows={props.rows} />
      </div>
    </Accordion>
  );
}

function DtoIssueBlocks({ rows }: { rows: BulkUploadIssueRowDto[] }) {
  return (
    <div className="flex max-h-60 flex-col gap-2 overflow-y-auto text-sm">
      {rows.map((err, idx) => (
        <div
          key={idx}
          className="flex items-start gap-2 rounded px-2 py-1 border-b border-solid border-neutral-border/50 last:border-0"
        >
          <FeatherAlertTriangle className="h-4 w-4 text-error-600 flex-shrink-0 mt-0.5" />
          <div className="flex flex-col gap-1">
            <span className="text-body font-body text-error-700 font-medium">
              {bulkUploadIssuePrimaryFromDto(err)}
            </span>
            {err.message?.trim() ? (
              <span className="text-body font-body text-error-600">
                {err.message}
              </span>
            ) : null}
          </div>
        </div>
      ))}
    </div>
  );
}

export default UploadSection;
